import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FileText, UserCheck, Ban, AlertTriangle, Scale, Info } from 'lucide-react';
import { Card } from '../components/ui/card';

const Section = ({ icon: Icon, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="mb-8"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <h2 className="text-2xl font-bold text-foreground">{title}</h2>
    </div>
    <div className="text-muted-foreground space-y-4">{children}</div>
  </motion.div>
);

export default function TermsOfService() {
  return (
    <>
      <Helmet>
        <title>Terms of Service - VisualMathLab</title>
        <meta name="description" content="VisualMathLab terms of service. Read our terms and conditions for using our interactive math calculators." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://visualmathlab.online/terms" />
      </Helmet>

      <div className="min-h-screen py-20 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-6">
              <Scale className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Terms of Service
            </h1>
            <p className="text-muted-foreground">
              Last Updated: January 2025
            </p>
          </motion.div>

          <Card className="p-8 md:p-12">
            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-8"
            >
              <p className="text-muted-foreground leading-relaxed">
                Welcome to VisualMathLab. By accessing or using our website and services, you agree to be 
                bound by these Terms of Service. Please read them carefully before using our platform.
              </p>
            </motion.div>

            {/* Acceptance of Terms */}
            <Section icon={UserCheck} title="Acceptance of Terms">
              <p>
                By accessing and using VisualMathLab ("the Service"), you accept and agree to be bound by 
                the terms and conditions of this agreement. If you do not agree to these terms, please do 
                not use our Service.
              </p>
              <p className="mt-4">
                These Terms apply to all visitors, users, and others who access or use the Service.
              </p>
            </Section>

            {/* Use of Service */}
            <Section icon={FileText} title="Use of Service">
              <p>
                VisualMathLab provides free interactive mathematical calculators and educational tools. 
                You may use our Service for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Personal educational purposes</li>
                <li>Academic study and homework assistance</li>
                <li>Teaching and classroom instruction</li>
                <li>General mathematical calculations</li>
              </ul>
              <p className="mt-4">
                You agree to use the Service only for lawful purposes and in accordance with these Terms.
              </p>
            </Section>

            {/* User Responsibilities */}
            <Section icon={Info} title="User Responsibilities">
              <p>When using our Service, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate information when contacting us</li>
                <li>Not attempt to gain unauthorized access to our systems</li>
                <li>Not use the Service for any illegal or unauthorized purpose</li>
                <li>Not interfere with or disrupt the Service or servers</li>
                <li>Not transmit any viruses, malware, or harmful code</li>
                <li>Not engage in any activity that could harm or overload our infrastructure</li>
                <li>Not scrape, copy, or redistribute our content without permission</li>
              </ul>
            </Section>

            {/* Intellectual Property */}
            <Section icon={FileText} title="Intellectual Property">
              <p>
                The Service and its original content, features, and functionality are owned by VisualMathLab 
                and are protected by international copyright, trademark, and other intellectual property laws.
              </p>
              <p className="mt-4">
                You may not reproduce, distribute, modify, create derivative works, publicly display, or 
                exploit any of our content without express written permission.
              </p>
              <p className="mt-4">
                However, you may:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use our calculators for personal and educational purposes</li>
                <li>Share links to our website</li>
                <li>Cite our tools in academic work with proper attribution</li>
              </ul>
            </Section>

            {/* Disclaimer of Warranties */}
            <Section icon={AlertTriangle} title="Disclaimer of Warranties">
              <p>
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND, 
                EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Accuracy or completeness of calculations</li>
                <li>Uninterrupted or error-free operation</li>
                <li>Fitness for a particular purpose</li>
                <li>Non-infringement of third-party rights</li>
              </ul>
              <p className="mt-4">
                While we strive for accuracy, mathematical calculations should always be independently 
                verified for critical applications.
              </p>
            </Section>

            {/* Limitation of Liability */}
            <Section icon={Ban} title="Limitation of Liability">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, VISUALMATHLAB SHALL NOT BE LIABLE FOR ANY:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, data, use, or goodwill</li>
                <li>Service interruptions or errors in calculations</li>
                <li>Damages resulting from reliance on information provided</li>
              </ul>
              <p className="mt-4">
                Our total liability shall not exceed the amount you paid us (if any) in the past twelve months.
              </p>
            </Section>

            {/* Educational Purpose */}
            <Section icon={Info} title="Educational Purpose Disclaimer">
              <p>
                VisualMathLab is designed as an educational tool to help users understand mathematical concepts. 
                Our calculators should:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Be used as learning aids, not as replacements for understanding</li>
                <li>Have results verified independently for critical applications</li>
                <li>Not be solely relied upon for exams, assignments, or professional work</li>
                <li>Be supplemented with proper mathematical education</li>
              </ul>
              <p className="mt-4">
                We encourage users to understand the mathematical principles behind the calculations.
              </p>
            </Section>

            {/* Third-Party Links */}
            <Section icon={FileText} title="Third-Party Links and Services">
              <p>
                Our Service may contain links to third-party websites or services that are not owned or 
                controlled by VisualMathLab. We have no control over, and assume no responsibility for, 
                the content, privacy policies, or practices of any third-party sites or services.
              </p>
            </Section>

            {/* Modifications to Service */}
            <Section icon={AlertTriangle} title="Modifications to Service">
              <p>
                We reserve the right to modify or discontinue, temporarily or permanently, the Service 
                (or any part thereof) with or without notice. We shall not be liable to you or any third 
                party for any modification, suspension, or discontinuance of the Service.
              </p>
            </Section>

            {/* Termination */}
            <Section icon={Ban} title="Termination">
              <p>
                We may terminate or suspend your access to our Service immediately, without prior notice, 
                for any reason, including breach of these Terms.
              </p>
              <p className="mt-4">
                Upon termination, your right to use the Service will immediately cease.
              </p>
            </Section>

            {/* Governing Law */}
            <Section icon={Scale} title="Governing Law">
              <p>
                These Terms shall be governed by and construed in accordance with applicable international 
                laws, without regard to conflict of law provisions.
              </p>
              <p className="mt-4">
                Any disputes arising from these Terms shall be resolved through good faith negotiations 
                or, if necessary, through appropriate legal channels.
              </p>
            </Section>

            {/* Changes to Terms */}
            <Section icon={FileText} title="Changes to These Terms">
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of any changes 
                by posting the new Terms on this page and updating the "Last Updated" date.
              </p>
              <p className="mt-4">
                Your continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </Section>

            {/* Contact */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mt-12 p-6 bg-muted/50 rounded-xl"
            >
              <h3 className="text-lg font-semibold text-foreground mb-2">Contact Us</h3>
              <p className="text-muted-foreground mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-2">
                <p className="text-sm">
                  <strong>Email:</strong> <a href="mailto:legal@visualmathlab.online" className="text-primary hover:underline">legal@visualmathlab.online</a>
                </p>
                <p className="text-sm">
                  <strong>Website:</strong> <a href="https://visualmathlab.online" className="text-primary hover:underline">https://visualmathlab.online</a>
                </p>
              </div>
            </motion.div>
          </Card>
        </div>
      </div>
    </>
  );
}